import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminLogin from "@/pages/AdminLogin";
import { Pencil, Trash2, X, Paperclip, LogOut, TrendingUp, Package, Euro, Search, Download, Archive } from "lucide-react";
import { API } from "@/lib/api";
import OrderDetailModal from "@/components/OrderDetailModal";

const EMPTY_FORM = { name: "", description: "", category: "", price_cents: "", images: [], sizes: [], stock: "", active: true, weight_grams: "200" };

const STATUS_LABELS = {
  paid: "Payée",
  processing: "En préparation",
  shipped: "Expédiée",
  delivered: "Livrée",
  cancelled: "Annulée",
};

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [orderQuery, setOrderQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const authorized = user && (user.role === "super_admin" || user.permissions?.manage_shop);

  const reload = () => {
    api.get("/admin/shop/products").then(({ data }) => setProducts(data)).catch(() => {});
    api.get("/admin/shop/orders", { params: { include_archived: true } }).then(({ data }) => setOrders(data)).catch(() => {});
    api.get("/admin/shop/stats").then(({ data }) => setStats(data)).catch(() => {});
  };

  useEffect(() => {
    if (authorized) reload();
  }, [authorized]);

  const visibleOrders = orders.filter((o) => {
    if (!showArchived && o.archived) return false;
    if (statusFilter !== "all" && (o.status || "paid") !== statusFilter) return false;
    const q = orderQuery.trim().toLowerCase();
    if (!q) return true;
    return (o.customer_name || "").toLowerCase().includes(q) || (o.customer_email || "").toLowerCase().includes(q);
  });

  if (loading) return <div className="text-center py-20 text-white/40">Chargement...</div>;
  if (!authorized) return <AdminLogin />;

  const editProduct = (p) => {
    setEditingId(p.product_id);
    setForm({ ...p, price_cents: String(p.price_cents), stock: p.stock === null || p.stock === undefined ? "" : String(p.stock), weight_grams: String(p.weight_grams || 200) });
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const saveProduct = async () => {
    if (!form.name.trim() || !form.price_cents) {
      toast.error("Nom et prix sont obligatoires");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        price_cents: Math.round(parseFloat(form.price_cents) * 100),
        stock: form.stock === "" ? null : parseInt(form.stock, 10),
        weight_grams: form.weight_grams === "" ? 200 : parseInt(form.weight_grams, 10),
      };
      if (editingId) {
        await api.put(`/admin/shop/products/${editingId}`, payload);
        toast.success("Produit mis à jour");
      } else {
        await api.post("/admin/shop/products", payload);
        toast.success("Produit créé");
      }
      resetForm();
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (p) => {
    if (!window.confirm(`Supprimer définitivement le produit "${p.name}" ?`)) return;
    try {
      await api.delete(`/admin/shop/products/${p.product_id}`);
      toast.success("Produit supprimé");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de la suppression");
    }
  };

  const uploadImage = async (file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/admin/shop/products/upload-image", formData);
      setForm((f) => ({ ...f, images: [...f.images, data.url] }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi de l'image");
    } finally {
      setUploading(false);
    }
  };

  const sizesText = (form.sizes || []).join(", ");

  return (
    <div className="max-w-6xl mx-auto px-5 pt-8 pb-16 space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-black text-2xl tracking-tight">Administration boutique</h1>
        <button onClick={logout} className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white transition">
          <LogOut size={14} /> Déconnexion
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="card-glass p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-full bg-[#39FF14]/10 flex items-center justify-center shrink-0"><Euro size={18} className="text-[#39FF14]" /></div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-white/40">Chiffre d'affaires</p>
              <p className="font-display font-black text-xl">{(stats.revenue_cents / 100).toFixed(2)}€</p>
            </div>
          </div>
          <div className="card-glass p-5 flex items-center gap-4">
            <div className="w-11 h-11 rounded-full bg-[#00F0FF]/10 flex items-center justify-center shrink-0"><Package size={18} className="text-[#00F0FF]" /></div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-white/40">Commandes</p>
              <p className="font-display font-black text-xl">{stats.order_count}</p>
            </div>
          </div>
          <div className="card-glass p-5">
            <div className="flex items-center gap-2 mb-2"><TrendingUp size={14} className="text-[#FF007F]" /><p className="text-[10px] uppercase tracking-widest text-white/40">Meilleures ventes</p></div>
            {stats.best_sellers.length === 0 ? <p className="text-xs text-white/30">Pas encore de vente.</p> : (
              <div className="space-y-1">
                {stats.best_sellers.slice(0, 3).map((b, i) => (
                  <p key={i} className="text-xs text-white/70 truncate">{b.product_name} <span className="text-white/40">× {b.quantity}</span></p>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="card-glass p-5">
            <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">30 derniers jours</p>
            <p className="text-sm">{(stats.revenue_30d_cents / 100).toFixed(2)}€ · {stats.orders_30d_count} commande(s)</p>
            <p className="text-xs text-white/40 mt-1">Panier moyen : {(stats.avg_order_cents / 100).toFixed(2)}€</p>
          </div>
          <div className="card-glass p-5">
            <p className="text-[10px] uppercase tracking-widest text-white/40 mb-2">Répartition par statut</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.status_breakdown).map(([status, count]) => count > 0 && (
                <span key={status} className="px-2.5 py-1 rounded-full bg-white/5 text-[11px] text-white/70">{STATUS_LABELS[status]} : {count}</span>
              ))}
              {stats.archived_count > 0 && <span className="px-2.5 py-1 rounded-full bg-white/5 text-[11px] text-white/40">Archivées : {stats.archived_count}</span>}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="card-glass p-6 space-y-3">
          <h2 className="font-display font-bold text-xl mb-2">{editingId ? "Modifier le produit" : "Nouveau produit"}</h2>
          <input placeholder="Nom du produit" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-[#14141A] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#00F0FF]" />
          <textarea placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full bg-[#14141A] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#00F0FF]" />
          <input placeholder="Catégorie (ex: Vêtements, Goodies...)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full bg-[#14141A] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#00F0FF]" />
          <div className="grid grid-cols-2 gap-3">
            <input type="number" step="0.01" min="0" placeholder="Prix (€)" value={form.price_cents} onChange={(e) => setForm({ ...form, price_cents: e.target.value })} className="w-full bg-[#14141A] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#00F0FF]" />
            <input type="number" min="0" placeholder="Stock (vide = illimité)" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="w-full bg-[#14141A] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#00F0FF]" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40 font-display mb-1 block">Poids unitaire (grammes - utilisé pour générer l'étiquette d'expédition)</label>
            <input type="number" min="1" placeholder="200" value={form.weight_grams} onChange={(e) => setForm({ ...form, weight_grams: e.target.value })} className="w-full bg-[#14141A] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#00F0FF]" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40 font-display mb-1 block">Tailles (séparées par une virgule - laisser vide si non applicable)</label>
            <input
              placeholder="S, M, L, XL"
              value={sizesText}
              onChange={(e) => setForm({ ...form, sizes: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              className="w-full bg-[#14141A] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#00F0FF]"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40 font-display mb-1 block">Photos</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {form.images.map((url, i) => (
                <div key={url} className="relative">
                  <img src={url} alt="" className="w-16 h-16 rounded-lg object-cover border border-white/10" />
                  <button onClick={() => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }))} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-[#FF0033] flex items-center justify-center">
                    <X size={12} />
                  </button>
                </div>
              ))}
            </div>
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 text-white/70 text-xs cursor-pointer hover:bg-white/10">
              <Paperclip size={14} /> {uploading ? "Envoi..." : "Ajouter une photo"}
              <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => e.target.files[0] && uploadImage(e.target.files[0])} />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm text-white/70">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            Produit visible sur la boutique
          </label>
          <div className="flex gap-2">
            <button onClick={saveProduct} disabled={saving} className="btn-neon flex-1 !py-3 disabled:opacity-50">
              {saving ? "..." : editingId ? "Mettre à jour" : "Créer le produit"}
            </button>
            {editingId && <button onClick={resetForm} className="btn-solid !py-3 !px-4">Annuler</button>}
          </div>
        </div>

        <div className="space-y-3">
          <h2 className="font-display font-bold text-xl">Produits ({products.length})</h2>
          {products.length === 0 ? <div className="text-white/40 text-sm">Aucun produit pour le moment.</div> : products.map((p) => (
            <div key={p.product_id} className="card-glass p-4 flex items-center gap-3">
              {p.images?.[0] ? <img src={p.images[0]} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" /> : <div className="w-14 h-14 rounded-lg bg-white/5 shrink-0" />}
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-bold truncate">{p.name}</h3>
                <p className="text-xs text-white/50">
                  {p.category ? `${p.category} · ` : ""}{(p.price_cents / 100).toFixed(2)}€ · {p.stock === null || p.stock === undefined ? "Stock illimité" : `${p.stock} en stock`}{!p.active && " · Masqué"}
                </p>
              </div>
              <button onClick={() => editProduct(p)} className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-white/70 hover:text-[#00F0FF] shrink-0"><Pencil size={14} /></button>
              <button onClick={() => deleteProduct(p)} className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-white/70 hover:text-[#FF0033] shrink-0"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-display font-bold text-xl">Commandes ({visibleOrders.length})</h2>
          <a href={`${API}/admin/shop/orders/export`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-white/50 hover:text-white transition">
            <Download size={14} /> Exporter en CSV
          </a>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/30" />
            <input
              value={orderQuery} onChange={(e) => setOrderQuery(e.target.value)} placeholder="Rechercher client·e ou email..."
              className="w-full bg-[#14141A] border border-white/10 rounded-full pl-9 pr-4 py-2 text-xs outline-none focus:border-[#00F0FF]"
            />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-[#14141A] border border-white/10 rounded-full px-3 py-2 text-xs outline-none focus:border-[#00F0FF] shrink-0">
            <option value="all">Tous les statuts</option>
            {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button
            onClick={() => setShowArchived((s) => !s)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs shrink-0 border transition-colors ${showArchived ? "bg-white/10 border-white/20 text-white" : "border-white/10 text-white/50 hover:border-white/30"}`}
          >
            <Archive size={14} /> Archivées
          </button>
        </div>

        {visibleOrders.length === 0 ? <div className="text-white/40 text-sm py-6">Aucune commande ne correspond.</div> : visibleOrders.map((o) => (
          <button key={o.order_id} onClick={() => setSelectedOrder(o)} className="w-full text-left card-glass p-4 hover:border-white/20 transition-all">
            <div className="flex justify-between items-start gap-3 flex-wrap">
              <div className="min-w-0">
                {(o.items || []).slice(0, 2).map((it, i) => (
                  <h3 key={i} className="font-display font-bold text-sm truncate">{it.product_name}{it.size && ` - ${it.size}`} × {it.quantity}</h3>
                ))}
                {(o.items || []).length > 2 && <p className="text-xs text-white/40">+ {o.items.length - 2} autre(s) article(s)</p>}
                <p className="text-xs text-white/50 mt-1">{o.customer_name} · {o.customer_email}</p>
              </div>
              <div className="text-right shrink-0 space-y-1">
                <span className="font-display font-bold text-[#39FF14]">{((o.amount_total_cents || 0) / 100).toFixed(2)}€</span>
                <p className="text-[10px] text-white/40">{new Date(o.created_at).toLocaleDateString("fr-FR")}</p>
                <span className="inline-block px-2 py-0.5 rounded-full bg-white/5 text-[10px] text-white/70">{STATUS_LABELS[o.status] || STATUS_LABELS.paid}{o.archived ? " · Archivée" : ""}</span>
              </div>
            </div>
          </button>
        ))}
      </div>

      {selectedOrder && (
        <OrderDetailModal order={selectedOrder} onClose={() => setSelectedOrder(null)} onChanged={reload} />
      )}
    </div>
  );
}
